import React, { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { styles } from './DriverStart.styles';
import Typography from 'src/components/Text/Text';
import InputField from 'src/components/input/input';
import Divider from 'src/components/Divider/Divider';
import Button from 'src/components/Button/Button';
import CameraCard from 'src/components/Cards/CameraCard';
import ImagePreview from 'src/components/ImagePreview/ImagePreview';
import IconInput from 'src/components/IconInput/IconInput';
import arrowDown from 'src/assets/arrowDown-3x.png';
import RadioModal from 'src/components/RadioModal/RadioModal';
import Toast from 'src/components/Toast/Toast';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getDriverLicenseTour } from 'src/features/tour/tourSteps';

import { useLinkTo, useRoute } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentDriver } from 'src/features/drivers/slice/driverSlice';
import { RootState } from 'src/store/store';
import { URLS } from 'src/constants/urls';
import { stateList } from './DriverStart.modalLists';
import { useHandleDlImageUpload } from 'src/features/drivers/utils/driver.hooks';
import { setActiveTab } from 'src/slices/UiSlice';
import { TABS_INDEX } from 'src/constants/tabs';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents'; // Import helper method

const DriverStart: React.FC = () => {
  const [dlNumber, setDlNumber] = useState('');
  const [dlNumberExists, setDlNumberExists] = useState(false);
  const [dlState, setDlState] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dlExtractorError, setDlExtractorError] = useState(false);

  const linkTo = useLinkTo();
  const dispatch = useDispatch();
  const { handleDlImageUpload } = useHandleDlImageUpload(setDlExtractorError); // Use the hook

  const currentDriver = useSelector((state: RootState) => state.drivers.currentDriver);
  const drivers = useSelector((state: RootState) => state.drivers.drivers);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  useEffect(() => {
    // Track Add Driver Page Landed event
    trackGTMEvent(
      'add-driver-page-landed',
      {
        userEvent: 'user lands on driver start screen',
      },
      accountType,
    );
  }, []);

  useEffect(() => {
    if (dlExtractorError) {
      setDlNumber('');
      setDlState('');
    }
  }, [dlExtractorError]);

  useEffect(() => {
    if (currentDriver) {
      setDlNumber(currentDriver.dlNumber || '');
      setDlState(currentDriver.dlState || '');
    }
  }, [currentDriver]);

  useEffect(() => {
    if (selectedImage) {
      // Track DL Extractor Image Uploaded event
      trackGTMEvent(
        'dl-extractor-image-uploaded',
        {
          userEvent: 'user uploaded an image for diriver lisence extraction',
          imageName: selectedImage,
        },
        accountType,
      );
      handleDlImageUpload(selectedImage); // Upload image when selectedImage is set
    }
  }, [selectedImage]);

  // Auto-start tour on first visit
  useTourAutoStart({
    tourKey: 'driverStart',
    driverSteps: getDriverLicenseTour(),
    enabled: dlNumber !== undefined && dlState !== undefined,
  });

  const isSaveEnabled = dlNumber.length >= 4 && dlState.length > 0;
  const route = useRoute();
  const { id } = route.params as { id?: number };

  const handleBack = () => {
    // Track Add Driver Page Left event
    trackGTMEvent(
      'driver-start-screen-left',
      {
        userEvent: 'user left the driver add screen by pressing back button',
        buttonTitle: 'Back',
      },
      accountType,
    );

    if (drivers.length) {
      linkTo(URLS.DRIVER.BYPASS);
    } else {
      linkTo(URLS.VEHICLE.BYPASS);
      dispatch(setActiveTab({ activeTab: TABS_INDEX.VEHICLE }));
    }
  };

  const handleNext = () => {
    // Track DL Given event
    trackGTMEvent(
      'dl-confirmed',
      {
        screenName: 'AddDriverPage',
        buttonTitle: 'Next',
        userEvent: 'user pressed next after filling DL details',
        dlNumber,
        dlState,
      },
      accountType,
    );

    if (id) {
      linkTo(`${URLS.DRIVER.EDIT}/${id}`);
      dispatch(setCurrentDriver({ ...currentDriver, dlNumber, dlState }));
    } else {
      const doesDlNumberExist = drivers.some((driver) => driver.dlNumber === dlNumber);
      setDlNumberExists(doesDlNumberExist);
      if (doesDlNumberExist) {
        setDlNumberExists(doesDlNumberExist);
        const timeout = setTimeout(() => {
          setDlNumberExists(false);
        }, 3000);
        return () => clearTimeout(timeout); // Cleanup timeout
      }
      dispatch(setCurrentDriver({ ...currentDriver, dlNumber, dlState }));
      linkTo(URLS.DRIVER.ADD);
    }
  };

  const handleImageUploadClick = (image: string) => {
    // Track DL Extractor Clicked event
    trackGTMEvent(
      'dl-extractor-clicked',
      {
        buttonTitle: 'Upload a photo of your driver’s license',
        userEvent: 'User pressed upload dl button ',
      },
      accountType,
    );

    setSelectedImage(image);
  };

  const handleDlNumberBlur = () => {
    if (dlNumber !== currentDriver?.dlNumber) {
      trackGTMEvent(
        'dl-input-field-edited',
        {
          fieldName: "Driver's License Number",
          previousValue: currentDriver?.dlNumber || '',
          newValue: dlNumber,
          editType: 'Manual DL Entry',
        },
        accountType,
      );
    }
  };

  const handleInputChange = (text: string) => {
    // Check for invalid characters in DL
    if (/[^A-Za-z0-9]/.test(text)) {
      return;
    }

    setDlNumber(text); // Update VIN state with cleaned value
  };

  return (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.container}>
      <View style={styles.topContainer}>
        <Typography variant="h2">Driver‘s License</Typography>
        {!selectedImage && (
          <TourWrapper tourId="upload-driver-license-card">
            <CameraCard
              testID="driver-license-upload-button"
              text="Upload a photo of your driver's license"
              fileType="image"
              onImageSelected={(imageUri) => handleImageUploadClick(imageUri as string)}
            />
          </TourWrapper>
        )}
        {selectedImage && (
          <ImagePreview
            testID="driver-license-image-preview"
            source={selectedImage}
            onIconPress={() => setSelectedImage(null)}
          />
        )}
        <Divider text=" Or " />
        <TourWrapper tourId="dl-number-input">
          <InputField
            variant="caption"
            testID="driver-license-driving-license-number"
            label={`Driver's License Number`}
            value={dlNumber}
            placeholder={`Please enter your driver's license number`}
            onChange={(text: string) => handleInputChange(text)}
            onBlur={handleDlNumberBlur}
            maxLength={18}
          />
        </TourWrapper>
        <TourWrapper tourId="dl-state-input">
          <IconInput
            label={`Driver's License State`}
            testID="driver-license-driving-license-state"
            value={dlState}
            placeholder={`Please enter your driver's license state`}
            onPress={() => {
              setIsVisible(true);
            }}
            iconSource={arrowDown}
            iconHeight={6.67}
            iconWidth={16.67}
          />
        </TourWrapper>
        <RadioModal
          testID={'driver-license-state-options'}
          isVisible={isVisible}
          onSelectOption={(value) => setDlState(value)}
          onButtonClickLeft={() => {
            setIsVisible(false);
          }}
          onClose={() => setIsVisible(false)}
          title={`Please enter your driver's license state`}
          options={stateList}
          selectedValue={dlState}
        />
      </View>
      <View>
        {dlNumberExists && (
          <Toast
            text={`A driver with this driver's license number has already been added to the list.`}
            type={'error'}
            animate={true}
          />
        )}
        {dlExtractorError && (
          <Toast
            text={`An error occurred while extracting Driver's Information from the image. Please upload valid image or input driver details manually.`}
            type={'error'}
            animate={true}
          />
        )}
        <View style={styles.buttonContainer}>
          <View style={styles.buttonWrapper1}>
            <Button type="tertiary" title="Back" onPress={handleBack} testID="drvr-licns-back" />
          </View>
          <View style={styles.buttonWrapper2}>
            <TourWrapper tourId="driver-license-next-btn">
              <Button
                testID="drvr-licns-next"
                type="primary"
                colorVariant={isSaveEnabled ? 'default' : 'disabled'}
                title="Next"
                onPress={handleNext}
              />
            </TourWrapper>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default DriverStart;
